import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  InputLabel,
  Select,
  TextField,
} from '@material-ui/core';
import React, { FC, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import Request, { RequestStatus, RequestUpdateOptions } from '../../../../../../interfaces/api/Request';
import { createUseStyles } from 'react-jss';

interface Props {
  open: boolean;
  request: Request;
  onClose: () => void;
  onValidate: (requestUpdateOptions: RequestUpdateOptions) => void;
}

const statusOptions = [
  RequestStatus.TAKING_INTO_ACCOUNT,
  RequestStatus.IN_PROCESS,
  RequestStatus.INTERVENTION_SCHEDULED,
  RequestStatus.WAITING_FOR_CUSTOMER_RETURN,
  RequestStatus.CLOSED,
];

const useStyles = createUseStyles({
  error: {
    color: '#f44336',
    fontSize: '.75rem',
    padding: '0 25px',
  },
});

const UpdateRequestDialog: FC<Props> = ({ open, request, onValidate, onClose }) => {
  const [formError, setFormError] = useState<string>();
  const [loading, setLoading] = useState(false);
  const classes = useStyles();

  const { control, watch, handleSubmit } = useForm<RequestUpdateOptions>({
    defaultValues: {
      status: request.status,
    },
  });

  const onSubmit = async (requestUpdateOptions: RequestUpdateOptions) => {
    try {
      setLoading(true);
      setFormError(undefined);
      await onValidate(requestUpdateOptions);
    } catch (e) {
      setFormError(e.message);
    }

    setLoading(false);
  };

  const needClosedReason = watch('status') === RequestStatus.CLOSED;

  return (
    <Dialog open={open} onClose={onClose} aria-labelledby='form-dialog-title'>
      <DialogTitle id='form-dialog-title'>Mettre à jour la demande</DialogTitle>

      <DialogContent>
        <DialogContentText>
          Le nouveau statut de la demande sera visible par le client sur son espace client.
        </DialogContentText>

        <InputLabel htmlFor='request-status-select' style={{ fontSize: 14 }}>
          Nouveau statut
        </InputLabel>

        <Controller
          name='status'
          control={control}
          rules={{ required: { value: true, message: 'Le champ est requis' } }}
          render={({ field }) => (
            <Select native fullWidth inputProps={{ id: 'request-status-select' }} {...field}>
              {statusOptions.map((option, key) => (
                <option value={option} key={key}>
                  {option}
                </option>
              ))}
            </Select>
          )}
        />

        <div style={{ display: needClosedReason ? 'initial' : 'none' }}>
          <InputLabel htmlFor='request-status-select' style={{ marginTop: 20, fontSize: 14 }}>
            Raison de la cloture
          </InputLabel>

          <Controller
            name='closedReason'
            control={control}
            rules={{
              validate: (value) => {
                if (!needClosedReason) return true;
                if (value) return true;
                return 'Le champ est requis';
              },
            }}
            render={({ field, fieldState: { error } }) => (
              <TextField {...field} fullWidth error={undefined !== error} helperText={error?.message} />
            )}
          />
        </div>
      </DialogContent>

      {formError && <p className={classes.error}>{formError}</p>}

      <DialogActions>
        <Button onClick={onClose} color='primary'>
          Annuler
        </Button>
        <Button onClick={handleSubmit(onSubmit)} disabled={loading} color='primary'>
          Mettre à jour {loading && <CircularProgress size={15} />}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UpdateRequestDialog;
